import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  User as UserIcon,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Briefcase,
  AlertTriangle,
  FileText,
  Search,
  Sparkles,
  Loader2,
  Plus,
  MessageSquareText,
  Columns3,
  ArrowLeft,
  Wrench,
  Home,
  Building2,
  Info,
  BarChart3,
  Settings,
  ClipboardList,
  BellRing,
  DollarSign,
  Flame,
  Zap,
  Fan,
  History,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { customersApi, jobsApi, pipelineApi, geoApi, tradeTypesApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";
import TimeWheelPicker, { formatTime12 } from "./TimeWheelPicker";

const TRADE_STYLE_MAP = {
  HVAC: { color: "from-blue-500 to-cyan-500", icon: Fan },
  Plumbing: { color: "from-sky-500 to-indigo-500", icon: Wrench },
  Electrical: { color: "from-amber-500 to-orange-500", icon: Zap },
  Gas: { color: "from-rose-500 to-red-500", icon: Flame },
};
const DEFAULT_TRADE_STYLE = { color: "from-slate-500 to-slate-600", icon: Briefcase };
const SUB_CATEGORY_ICONS = {
  "Service Call": Phone,
  Maintenance: Settings,
  Installation: Wrench,
  Inspection: ClipboardList,
  Emergency: BellRing,
  Sales: DollarSign,
};
const DEFAULT_SUBCATEGORY_ICON = Briefcase;
const PRIORITIES = [
  { value: "Normal", className: "bg-slate-50 text-slate-700 border-slate-200", ring: "ring-slate-400" },
  { value: "High", className: "bg-amber-50 text-amber-700 border-amber-200", ring: "ring-amber-400" },
  { value: "Urgent", className: "bg-red-50 text-red-700 border-red-200", ring: "ring-red-400" },
];

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextHourTime() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function addHoursToTime(timeStr, hours) {
  if (!timeStr) return "";
  const [hh, mm] = timeStr.split(":").map(Number);
  let total = hh * 60 + mm + Math.round(hours * 60);
  total = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function addHoursTo(dateStr, timeStr, hours) {
  if (!dateStr || !timeStr || !hours) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const start = new Date(y, m - 1, d, hh, mm);
  const end = new Date(start.getTime() + Math.round(hours * 60) * 60 * 1000);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}T${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
}

export default function NewJobModal({ open, onClose, onCreated, prefillCustomer = null }) {
  // Customer
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [customerType, setCustomerType] = useState("Residential");
  // Job
  const [title, setTitle] = useState("");
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [showTitleSuggest, setShowTitleSuggest] = useState(false);
  const [loadingTitle, setLoadingTitle] = useState(false);
  const titleDebounceRef = useRef(null);
  const titleBlurRef = useRef(null);
  const [trade, setTrade] = useState("HVAC");
  const [subCategory, setSubCategory] = useState("Service Call");
  const [tradeTypes, setTradeTypes] = useState([]);
  const [priority, setPriority] = useState("Normal");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(todayISO());
  const [startTime, setStartTime] = useState(nextHourTime());
  const [duration, setDuration] = useState(2);
  // Arrival window — the promised customer arrival slot (e.g. 8:00–10:00 AM)
  const [arrivalStartTime, setArrivalStartTime] = useState(nextHourTime());
  const [arrivalEndTime, setArrivalEndTime] = useState(addHoursToTime(nextHourTime(), 2));
  // Mobile-only custom time picker (native <input type="time"> AM/PM can't be styled)
  const [timePickerField, setTimePickerField] = useState(null); // 'start' | 'arrivalStart' | 'arrivalEnd' | null
  const handleStartTimeChange = (v) => {
    setStartTime(v);
    setArrivalStartTime(v);
    setArrivalEndTime(addHoursToTime(v, 2));
  };
  // Pipeline picker (optional) — pick any pipeline (system or custom) to also drop
  // this new job onto that Kanban board. When the job's status changes later, the
  // linked card auto-moves to the stage whose name matches the new status.
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("jobs");
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineStageId, setPipelineStageId] = useState("");
  // Autocomplete + loading
  const [customers, setCustomers] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Post-save "send confirmation text?" popup
  const [confirmJob, setConfirmJob] = useState(null);
  const [sendingText, setSendingText] = useState(false);
  // Address autocomplete (OSM Nominatim via backend proxy)
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSug, setShowAddressSug] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const addressDebounceRef = useRef(null);
  const addressBlurRef = useRef(null);
  const wrapRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    customersApi
      .list()
      .then(setCustomers)
      .catch(() => setCustomers([]));
    // Load available pipelines (system + custom). Admin can drop the new job
    // onto any of them; for the system 'Jobs' board it appears automatically.
    // Note: The system 'Sales' board is intentionally NOT offered here — it's
    // backed by estimates (quotes), not jobs. Offering it would create a
    // starting-stage picker that never places the job on the sales board.
    pipelineApi
      .pipelines()
      .then((custom) => {
        setPipelines([
          { id: "jobs", name: "Jobs", color: "#2563EB", system: true },
          ...(Array.isArray(custom) ? custom.filter((p) => p.id !== "jobs").map((p) => ({ ...p, system: false })) : []),
        ]);
      })
      .catch(() => {
        setPipelines([
          { id: "jobs", name: "Jobs", color: "#2563EB", system: true },
        ]);
      });
    // Focus name field shortly after the modal mounts
    setTimeout(() => nameInputRef.current?.focus(), 80);
  }, [open]);

  // Whenever the picked pipeline changes, load its stage names so we can render
  // them under the dropdown and let the admin choose the entry stage.
  useEffect(() => {
    if (!open || !pipelineId) return;
    let cancelled = false;
    pipelineApi
      .stages(pipelineId)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setPipelineStages(list);
        setPipelineStageId((prev) => {
          const stillValid = list.find((s) => s.id === prev);
          if (stillValid) return prev;
          return list[0]?.id || "";
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPipelineStages([]);
          setPipelineStageId("");
        }
      });
    return () => { cancelled = true; };
  }, [open, pipelineId]);

  // Close suggest list on outside click
  useEffect(() => {
    if (!showSuggest) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showSuggest]);

  // Trades/sub-categories come from company settings (Company Settings ->
  // Trade Types) so custom trades show up here automatically — refetched
  // every time the modal opens so edits made elsewhere apply immediately.
  useEffect(() => {
    if (!open) return;
    tradeTypesApi.get().then((d) => setTradeTypes(d.trades || [])).catch(() => setTradeTypes([]));
  }, [open]);

  // If the selected trade no longer exists (deleted in settings) or its
  // sub-category list no longer includes the current pick, fall back to the
  // first available trade/sub-category.
  useEffect(() => {
    if (!tradeTypes.length) return;
    const current = tradeTypes.find((t) => t.name === trade);
    if (!current) {
      setTrade(tradeTypes[0].name);
      setSubCategory(tradeTypes[0].subCategories?.[0] || "");
    } else if (!(current.subCategories || []).includes(subCategory)) {
      setSubCategory(current.subCategories?.[0] || "");
    }
  }, [tradeTypes, trade, subCategory]);

  // Reset form when modal closes
  useEffect(() => {
    if (open) return;
    setCustomerName("");
    setCustomerId(null);
    setPhone("");
    setAddress("");
    setCity("");
    setState("");
    setZip("");
    setCustomerType("Residential");
    setTitle("");
    setTrade(tradeTypes[0]?.name || "HVAC");
    setSubCategory(tradeTypes[0]?.subCategories?.[0] || "Service Call");
    setPriority("Normal");
    setDescription("");
    setDate(todayISO());
    setStartTime(nextHourTime());
    setDuration(2);
    setArrivalStartTime(nextHourTime());
    setArrivalEndTime(addHoursToTime(nextHourTime(), 2));
    setPipelineId("jobs");
    setPipelineStageId("");
    setError("");
    setConfirmJob(null);
    setSendingText(false);
  }, [open]);

  // Prefill the customer block when launched from a specific customer's profile.
  useEffect(() => {
    if (!open || !prefillCustomer) return;
    setCustomerName(prefillCustomer.name || "");
    setCustomerId(prefillCustomer.id || null);
    setPhone(prefillCustomer.phone || "");
    setAddress(prefillCustomer.address || "");
    setCity(prefillCustomer.city || "");
    setState(prefillCustomer.state || "");
    setZip(prefillCustomer.zip || "");
    setCustomerType(prefillCustomer.type || "Residential");
  }, [open, prefillCustomer]);

  const suggestions = useMemo(() => {
    const q = customerName.trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter((c) => c.name?.toLowerCase().includes(q))
      .slice(0, 6);
  }, [customerName, customers]);

  const isExistingCustomer = !!customerId;

  // Mobile "Existing Customer" / "New Customer" pill toggle — purely a visual
  // shortcut onto behavior the form already supports: focusing the search
  // field, or detaching a picked customer (same as editing the name field).
  const focusExistingSearch = () => {
    nameInputRef.current?.focus();
    setShowSuggest(true);
  };
  const switchToNewCustomer = () => setCustomerId(null);

  const pickCustomer = (c) => {
    setCustomerName(c.name);
    setCustomerId(c.id);
    setPhone(c.phone || "");
    setAddress(c.address || "");
    setCity(c.city || "");
    setState(c.state || "");
    setZip(c.zip || "");
    setCustomerType(c.type || "Residential");
    setShowSuggest(false);
  };

  // If user edits the name field after a pick, detach the customerId
  const onNameChange = (val) => {
    setCustomerName(val);
    if (customerId) setCustomerId(null);
    setShowSuggest(true);
  };

  // ---- Job title autocomplete (per-company history of previously used titles) ----
  const onTitleChange = (value) => {
    setTitle(value);
    if (titleDebounceRef.current) clearTimeout(titleDebounceRef.current);
    setShowTitleSuggest(true);
    setLoadingTitle(true);
    titleDebounceRef.current = setTimeout(async () => {
      try {
        const results = await jobsApi.titleSuggestions(value.trim());
        setTitleSuggestions(results || []);
      } catch {
        setTitleSuggestions([]);
      } finally {
        setLoadingTitle(false);
      }
    }, 250);
  };

  const pickTitleSuggestion = (t) => {
    setTitle(t);
    setShowTitleSuggest(false);
  };

  // ---- Address autocomplete (OSM Nominatim via backend proxy) ----
  const onAddressChange = (value) => {
    setAddress(value);
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    if (value.trim().length < 3) {
      setAddressSuggestions([]);
      setShowAddressSug(false);
      return;
    }
    setLoadingAddress(true);
    setShowAddressSug(true);
    addressDebounceRef.current = setTimeout(async () => {
      try {
        const results = await geoApi.autocomplete(value.trim());
        setAddressSuggestions(results || []);
      } catch {
        setAddressSuggestions([]);
      } finally {
        setLoadingAddress(false);
      }
    }, 350);
  };

  const pickAddressSuggestion = (s) => {
    if (s.address) setAddress(s.address);
    if (s.city) setCity(s.city);
    if (s.state) setState(s.state);
    if (s.zip) setZip(s.zip);
    setShowAddressSug(false);
    setAddressSuggestions([]);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!customerName.trim()) return setError("Customer name is required");
    if (!address.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      return setError("Address, city, state, and zip are required");
    }
    setSubmitting(true);
    try {
      // 1) Resolve customer: existing → use id (and sync any edited fields back), else auto-create
      let resolvedId = customerId;
      if (!resolvedId) {
        const created = await customersApi.create({
          name: customerName.trim(),
          type: customerType,
          phone: phone.trim(),
          address: address.trim(),
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
        });
        resolvedId = created.id;
      } else {
        // Existing customer — sync any field the user edited in the modal back to the
        // customer profile so address/phone updates persist. Only PATCH if something
        // actually changed (avoid noisy writes when the user only picked & saved).
        const original = customers.find((c) => c.id === customerId);
        if (original) {
          const patch = {};
          if (phone.trim() !== (original.phone || "")) patch.phone = phone.trim();
          if (address.trim() !== (original.address || "")) patch.address = address.trim();
          if (city.trim() !== (original.city || "")) patch.city = city.trim();
          if (state.trim().toUpperCase() !== (original.state || "")) patch.state = state.trim().toUpperCase();
          if (zip.trim() !== (original.zip || "")) patch.zip = zip.trim();
          if (Object.keys(patch).length > 0) {
            try {
              await customersApi.update(customerId, patch);
            } catch (e) {
              console.warn("Customer profile update failed (continuing with job save):", e);
            }
          }
        }
      }

      const startIso = `${date}T${startTime}`;
      const durationHrs = parseFloat(duration) || 0.5;
      const endIso = addHoursTo(date, startTime, durationHrs);
      const arrivalStartIso = arrivalStartTime ? `${date}T${arrivalStartTime}` : null;
      const arrivalEndIso = arrivalEndTime ? `${date}T${arrivalEndTime}` : null;

      // When the admin picks a starting stage on the system 'Jobs' board, use
      // that stage's underlying status as the job's initial status so the new
      // job actually lands in the picked column on the Jobs pipeline board.
      // Custom pipeline picks leave the status alone (Unassigned) and place a
      // linked pipeline_card in the chosen stage instead.
      let initialStatus = "Unassigned";
      if (pipelineId === "jobs" && pipelineStageId) {
        const picked = pipelineStages.find((s) => s.id === pipelineStageId);
        if (picked?.status) initialStatus = picked.status;
      }

      // 2) Create the job. Tech is always Unassigned at creation — dispatcher
      // assigns from the Dispatch board. The initialStatus above lets the
      // admin drop the job into the correct Jobs-board column immediately.
      const job = await jobsApi.create({
        customer: customerName.trim(),
        customerId: resolvedId,
        phone: phone.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip: zip.trim(),
        trade,
        type: subCategory,
        title: title.trim(),
        priority,
        status: initialStatus,
        start: startIso,
        end: endIso,
        arrivalStart: arrivalStartIso,
        arrivalEnd: arrivalEndIso,
        durationMin: Math.round(durationHrs * 60),
        tech: "Unassigned",
        techId: null,
        description: description.trim(),
        // Ship the pipeline pick along so the backend can seed the linked card.
        pipelineId: pipelineId || null,
        pipelineStageId: pipelineStageId || null,
      });
      const queueLabel = initialStatus === "Unassigned" ? "Unassigned queue" : `${initialStatus} column`;
      toast.success(`Job ${job.id} created for ${customerName.trim()} — in ${queueLabel}`);
      if (phone.trim()) {
        // Ask before closing whether to text the customer a booking confirmation.
        setConfirmJob(job);
      } else {
        onCreated && onCreated(job);
        onClose && onClose();
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const finishAfterConfirm = (job) => {
    setConfirmJob(null);
    onCreated && onCreated(job);
    onClose && onClose();
  };

  const sendConfirmText = async () => {
    const job = confirmJob;
    setSendingText(true);
    try {
      await jobsApi.sendConfirmation(job.id);
      toast.success(`Confirmation text sent to ${phone.trim()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Text could not be sent");
    } finally {
      setSendingText(false);
      finishAfterConfirm(job);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6 max-[767px]:bg-transparent max-[767px]:backdrop-blur-none max-[767px]:p-0 max-[767px]:items-stretch max-[767px]:justify-stretch">
      <div id="mobile-create-job-card" className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col border border-slate-200 max-[767px]:rounded-none max-[767px]:shadow-none max-[767px]:border-0 max-[767px]:max-w-none max-[767px]:max-h-none max-[767px]:h-full max-[767px]:bg-gradient-to-b max-[767px]:from-[#1484F6] max-[767px]:to-[#FAFBFC]">
        {/* Header (desktop) */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50 max-[767px]:hidden">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Plus className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-slate-900">New Job</div>
              <div className="text-[11px] text-slate-500">Schedule a job for an existing or new customer</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="new-job-close"
            className="h-7 w-7 rounded hover:bg-white/60 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Header (mobile) */}
        <div className="hidden max-[767px]:flex items-center justify-between px-4 pt-5 pb-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="new-job-close-mobile"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/15 active:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5 text-white" strokeWidth={2.5} />
          </button>
          <div className="text-[17px] font-bold text-white">Create Job</div>
          <div className="h-9 w-9" />
        </div>

        {/* Hero banner (mobile) */}
        <div className="hidden max-[767px]:flex items-start gap-3 mx-4 mb-3 p-4 rounded-2xl bg-white/95 border border-white/60 shadow-sm">
          <div className="h-9 w-9 shrink-0 rounded-full bg-[#1484F6]/10 flex items-center justify-center">
            <Plus className="h-5 w-5 text-[#1484F6]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#173B89] leading-snug">Schedule a job for an existing or new customer</div>
            <div className="text-[12px] text-slate-500 mt-0.5">Search for a customer or enter new customer details.</div>
          </div>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-5 max-[767px]:px-4 max-[767px]:pt-0 max-[767px]:space-y-4" data-testid="new-job-form">
          {/* Customer block */}
          <section className="space-y-3 max-[767px]:bg-white max-[767px]:rounded-2xl max-[767px]:shadow-md max-[767px]:p-4 max-[767px]:space-y-4">
            <SectionHeader
              icon={UserIcon}
              label="Customer"
              mobileLabel="Customer Information"
              hint={isExistingCustomer ? "Existing customer linked" : "New customer will be created on save"}
              hintTone={isExistingCustomer ? "blue" : "amber"}
              mobileToggle={
                <CustomerModeToggle
                  isExisting={isExistingCustomer}
                  onPickExisting={focusExistingSearch}
                  onPickNew={switchToNewCustomer}
                />
              }
            />

            <div ref={wrapRef} className="relative">
              <Field label="Customer name" icon={Search} mobileIcon={UserIcon}>
                <input
                  ref={nameInputRef}
                  type="text"
                  required
                  data-testid="new-job-customer-name"
                  value={customerName}
                  onChange={(e) => onNameChange(e.target.value)}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="Start typing to search existing customers…"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
                  autoComplete="off"
                />
              </Field>
              {showSuggest && suggestions.length > 0 && (
                <div
                  data-testid="new-job-customer-suggest"
                  className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
                >
                  {suggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pickCustomer(c)}
                      data-testid={`new-job-suggest-${c.id}`}
                      className="w-full px-3 py-2 text-left hover:bg-blue-50/60 transition-colors border-b last:border-b-0 border-slate-50"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-slate-800 truncate">{c.name}</div>
                          <div className="text-[11.5px] text-slate-500 truncate">
                            {c.address}, {c.city} · {c.phone}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            c.type === "Commercial"
                              ? "bg-fuchsia-50 text-fuchsia-700"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {c.type}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showSuggest && customerName.trim() && suggestions.length === 0 && (
                <div
                  className="absolute z-10 left-0 right-0 mt-1 rounded-lg border border-amber-300 px-3 py-2 text-[12px] flex items-center gap-1.5"
                  style={{ backgroundColor: "#ffffff", color: "#111827" }}
                >
                  <Sparkles className="h-3.5 w-3.5" style={{ color: "#b45309" }} />
                  <span style={{ color: "#111827" }}>
                    No match. A new customer &quot;<strong style={{ color: "#111827" }}>{customerName.trim()}</strong>&quot; will be created.
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 max-[767px]:grid-cols-1 max-[767px]:gap-3">
              <Field label="Phone" icon={Phone} mobileClassName="max-[767px]:order-2">
                <input
                  type="tel"
                  data-testid="new-job-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
                />
              </Field>
              <div className="max-[767px]:order-1">
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Customer type</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { value: "Residential", icon: Home },
                    { value: "Commercial", icon: Building2 },
                  ].map(({ value: t, icon: TIcon }) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setCustomerType(t)}
                      data-testid={`new-job-customer-type-${t.toLowerCase()}`}
                      disabled={isExistingCustomer}
                      className={`px-2 py-2 rounded-lg border text-[12.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 max-[767px]:py-2.5 max-[767px]:rounded-xl ${
                        customerType === t
                          ? "border-blue-400 bg-blue-50/60 text-blue-700 max-[767px]:bg-white max-[767px]:border-[#1484F6] max-[767px]:text-[#1484F6]"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 max-[767px]:bg-slate-100 max-[767px]:border-slate-100 max-[767px]:text-slate-600"
                      }`}
                    >
                      <TIcon className="h-3.5 w-3.5 hidden max-[767px]:block" />
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Field label="Address" icon={MapPin}>
              <div className="relative">
                <input
                  type="text"
                  required
                  data-testid="new-job-address"
                  value={address}
                  onChange={(e) => onAddressChange(e.target.value)}
                  onFocus={() => { if (addressSuggestions.length) setShowAddressSug(true); }}
                  onBlur={() => {
                    addressBlurRef.current = setTimeout(() => setShowAddressSug(false), 150);
                  }}
                  placeholder="Start typing an address…"
                  autoComplete="off"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
                />
                {showAddressSug && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-lg border shadow-lg"
                    data-testid="new-job-address-suggestions"
                    style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (addressBlurRef.current) clearTimeout(addressBlurRef.current);
                    }}
                  >
                    {loadingAddress && (
                      <div className="px-3 py-2.5 text-[12.5px] flex items-center gap-2" style={{ color: "#64748b" }}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching addresses…
                      </div>
                    )}
                    {!loadingAddress && addressSuggestions.length === 0 && (
                      <div className="px-3 py-2.5 text-[12.5px]" style={{ color: "#64748b" }}>No matches found</div>
                    )}
                    {!loadingAddress && addressSuggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => pickAddressSuggestion(s)}
                        data-testid={`new-job-address-suggestion-${i}`}
                        className="w-full px-3 py-2 text-left border-b last:border-b-0 flex items-start gap-2"
                        style={{ borderColor: "#f1f5f9", color: "#111827" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#ecfdf5"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffffff"; }}
                      >
                        <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#64748b" }} />
                        <span className="text-[12.5px] leading-snug" style={{ color: "#111827" }}>{s.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-3">
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">City</label>
                <div className="relative">
                  <Building2 className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none hidden max-[767px]:block" />
                  <input
                    type="text"
                    required
                    data-testid="new-job-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:pl-9 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:rounded-xl"
                  />
                </div>
              </div>
              <div className="col-span-1">
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">State</label>
                <input
                  type="text"
                  required
                  maxLength={2}
                  data-testid="new-job-state"
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="AZ"
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:rounded-xl"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">ZIP</label>
                <input
                  type="text"
                  required
                  data-testid="new-job-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="85003"
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
                />
              </div>
            </div>
          </section>

          {/* Job block */}
          <section className="space-y-3 max-[767px]:bg-white max-[767px]:rounded-2xl max-[767px]:shadow-md max-[767px]:p-4 max-[767px]:space-y-4">
            <SectionHeader icon={Briefcase} mobileIcon={Wrench} label="Job details" mobileLabel="Job Details" />

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Job title</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  data-testid="new-job-title"
                  value={title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  onFocus={() => onTitleChange(title)}
                  onBlur={() => {
                    titleBlurRef.current = setTimeout(() => setShowTitleSuggest(false), 150);
                  }}
                  placeholder="e.g. AC not cooling, Annual furnace inspection…"
                  autoComplete="off"
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
                />
                {showTitleSuggest && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg border shadow-lg"
                    data-testid="new-job-title-suggestions"
                    style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (titleBlurRef.current) clearTimeout(titleBlurRef.current);
                    }}
                  >
                    {loadingTitle && (
                      <div className="px-3 py-2.5 text-[12.5px] flex items-center gap-2" style={{ color: "#64748b" }}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching previous titles…
                      </div>
                    )}
                    {!loadingTitle && titleSuggestions.length === 0 && (
                      <div className="px-3 py-2.5 text-[12.5px]" style={{ color: "#64748b" }} data-testid="new-job-title-empty-state">
                        {title.trim() ? "No matching titles yet — this will be saved as a new one." : "No previous job titles yet."}
                      </div>
                    )}
                    {!loadingTitle && titleSuggestions.map((t, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => pickTitleSuggestion(t)}
                        data-testid={`new-job-title-suggestion-${i}`}
                        className="w-full px-3 py-2 text-left border-b last:border-b-0 flex items-center gap-2"
                        style={{ borderColor: "#f1f5f9", color: "#111827" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#eff6ff"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffffff"; }}
                      >
                        <History className="h-3.5 w-3.5 shrink-0" style={{ color: "#64748b" }} />
                        <span className="text-[12.5px] leading-snug truncate" style={{ color: "#111827" }}>{t}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Trade</label>
              <div className="grid grid-cols-4 gap-2" data-testid="new-job-trade-picker">
                {tradeTypes.map((t) => {
                  const style = TRADE_STYLE_MAP[t.name] || DEFAULT_TRADE_STYLE;
                  const Icon = style.icon;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => {
                        setTrade(t.name);
                        setSubCategory(t.subCategories?.[0] || "");
                      }}
                      data-testid={`new-job-trade-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`relative px-2 py-2.5 rounded-lg border text-[12.5px] font-semibold transition-all overflow-hidden max-[767px]:flex max-[767px]:flex-col max-[767px]:items-center max-[767px]:gap-1 max-[767px]:px-0.5 max-[767px]:py-3 max-[767px]:rounded-xl ${
                        trade === t.name
                          ? "border-blue-400 ring-2 ring-blue-500/20 text-white max-[767px]:ring-0 max-[767px]:border-[#1484F6]"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 max-[767px]:bg-[#ffffff] max-[767px]:border-slate-200"
                      }`}
                    >
                      {trade === t.name && (
                        <span
                          className={`absolute inset-0 bg-gradient-to-br ${style.color} opacity-100 max-[767px]:!bg-none max-[767px]:!bg-[#1484F6]`}
                          aria-hidden="true"
                        />
                      )}
                      <Icon className="h-4 w-4 relative hidden max-[767px]:block" />
                      <span className="relative max-[767px]:text-[9.5px] max-[767px]:leading-tight max-[767px]:text-center max-[767px]:whitespace-normal max-[767px]:break-words">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Sub-category</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 max-[767px]:grid-cols-2 max-[767px]:gap-2" data-testid="new-job-subcategory-picker">
                {(tradeTypes.find((t) => t.name === trade)?.subCategories || []).map((s) => {
                  const SIcon = SUB_CATEGORY_ICONS[s] || DEFAULT_SUBCATEGORY_ICON;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSubCategory(s)}
                      data-testid={`new-job-sub-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-1.5 py-1.5 rounded-md border text-[11.5px] font-semibold max-[767px]:flex max-[767px]:items-center max-[767px]:justify-start max-[767px]:gap-1.5 max-[767px]:px-3 max-[767px]:py-2.5 max-[767px]:rounded-xl max-[767px]:text-[12.5px] ${
                        subCategory === s
                          ? "border-blue-400 bg-blue-50/60 text-blue-700 max-[767px]:bg-white max-[767px]:border-[#1484F6] max-[767px]:text-[#1484F6]"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 max-[767px]:bg-slate-100 max-[767px]:border-slate-100 max-[767px]:text-slate-600"
                      }`}
                    >
                      <SIcon className="h-3.5 w-3.5 hidden max-[767px]:block" />
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pipeline picker — drop this new job onto any pipeline board. When the
                job's status changes later, the card auto-moves to the stage whose
                name matches the new status. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 max-[767px]:bg-blue-50/70 max-[767px]:border-blue-100 max-[767px]:rounded-2xl max-[767px]:p-4" data-testid="new-job-pipeline-block">
              <label className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2 max-[767px]:text-[#173B89] max-[767px]:flex-wrap">
                <Columns3 className="h-3.5 w-3.5 text-blue-500 max-[767px]:hidden" />
                <BarChart3 className="h-4 w-4 text-[#1484F6] hidden max-[767px]:block" />
                Pipeline <span className="hidden max-[767px]:inline">(Optional)</span>
                <span className="text-[10.5px] font-normal text-slate-400 max-[767px]:basis-full max-[767px]:text-[11px] max-[767px]:mt-0.5 max-[767px]:font-normal max-[767px]:text-[#173B89]/70">— optional, auto-updates on status change</span>
              </label>
              <div className="grid grid-cols-2 gap-3 max-[767px]:grid-cols-1 max-[767px]:gap-2.5">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Board</div>
                  <select
                    value={pipelineId}
                    onChange={(e) => setPipelineId(e.target.value)}
                    data-testid="new-job-pipeline-select"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:rounded-xl"
                  >
                    <option value="">— No pipeline —</option>
                    {pipelines.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.system ? " (system)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Starting stage</div>
                  <select
                    value={pipelineStageId}
                    onChange={(e) => setPipelineStageId(e.target.value)}
                    disabled={!pipelineId || pipelineStages.length === 0}
                    data-testid="new-job-pipeline-stage-select"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13.5px] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:rounded-xl"
                  >
                    {pipelineStages.length === 0 && <option value="">—</option>}
                    {pipelineStages.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {pipelineId && pipelineStages.length > 0 && (
                <div className="mt-2.5" data-testid="new-job-pipeline-stage-chips">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                    Available statuses on this pipeline
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {pipelineStages.map((s) => (
                      <span
                        key={s.id}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-semibold ${
                          s.id === pipelineStageId
                            ? "bg-blue-50 text-blue-700 border-blue-200"
                            : "bg-white text-slate-600 border-slate-200"
                        }`}
                        data-testid={`new-job-pipeline-stage-chip-${s.id}`}
                      >
                        {s.name}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1.5 text-[10.5px] text-slate-400">
                    {pipelineId === "jobs"
                      ? "System board — this job will start under the picked column and auto-move as the status changes."
                      : "A card is created in the selected stage. It auto-moves whenever the job's status changes to a matching stage name."}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Priority</label>
              <div className="grid grid-cols-3 gap-1.5" data-testid="new-job-priority-picker">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    data-testid={`new-job-priority-${p.value.toLowerCase()}`}
                    className={`px-2 py-1.5 rounded-md border text-[12.5px] font-semibold transition-all ${p.className} ${
                      priority === p.value ? `ring-2 ${p.ring}/40 brightness-105` : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    {p.value === "Urgent" && <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" />}
                    {p.value}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 max-[767px]:grid-cols-2">
              <Field label="Date" icon={Calendar}>
                <input
                  type="date"
                  required
                  data-testid="new-job-date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full h-10 pl-9 pr-2 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:rounded-xl"
                />
              </Field>
              <Field label="Start time" icon={Clock}>
                <input
                  type="time"
                  required
                  data-testid="new-job-start-time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className="w-full h-10 pl-9 pr-2 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:hidden"
                />
                <button
                  type="button"
                  onClick={() => setTimePickerField("start")}
                  data-testid="new-job-start-time-mobile-btn"
                  className="hidden max-[767px]:flex w-full h-10 pl-9 pr-2 rounded-xl border border-slate-200 bg-white text-[13.5px] text-slate-700 items-center text-left font-semibold"
                >
                  {formatTime12(startTime) || "Select time"}
                </button>
              </Field>
              <div className="max-[767px]:col-span-2">
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">Duration (hrs)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step={0.5}
                  max={24}
                  required
                  data-testid="new-job-duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:rounded-xl"
                />
              </div>
            </div>

            {/* Arrival window — promised customer slot, shown to the tech in the mobile job view */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-2">
                <Clock className="h-3.5 w-3.5 text-blue-500" /> Arrival window
                <span className="text-[10.5px] font-normal text-slate-400">— shown to the technician</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Starts</label>
                  <input
                    type="time"
                    data-testid="new-job-arrival-start"
                    value={arrivalStartTime}
                    onChange={(e) => setArrivalStartTime(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:hidden"
                  />
                  <button
                    type="button"
                    onClick={() => setTimePickerField("arrivalStart")}
                    data-testid="new-job-arrival-start-mobile-btn"
                    className="hidden max-[767px]:flex w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-[13.5px] text-slate-700 items-center text-left font-semibold"
                  >
                    {formatTime12(arrivalStartTime) || "Select time"}
                  </button>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Ends</label>
                  <input
                    type="time"
                    data-testid="new-job-arrival-end"
                    value={arrivalEndTime}
                    onChange={(e) => setArrivalEndTime(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:hidden"
                  />
                  <button
                    type="button"
                    onClick={() => setTimePickerField("arrivalEnd")}
                    data-testid="new-job-arrival-end-mobile-btn"
                    className="hidden max-[767px]:flex w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-[13.5px] text-slate-700 items-center text-left font-semibold"
                  >
                    {formatTime12(arrivalEndTime) || "Select time"}
                  </button>
                </div>
              </div>
            </div>

            <TimeWheelPicker
              open={timePickerField === "start"}
              value={startTime}
              allowClear={false}
              onClose={() => setTimePickerField(null)}
              onChange={handleStartTimeChange}
            />
            <TimeWheelPicker
              open={timePickerField === "arrivalStart"}
              value={arrivalStartTime}
              allowClear
              onClose={() => setTimePickerField(null)}
              onChange={setArrivalStartTime}
            />
            <TimeWheelPicker
              open={timePickerField === "arrivalEnd"}
              value={arrivalEndTime}
              allowClear
              onClose={() => setTimePickerField(null)}
              onChange={setArrivalEndTime}
            />

            <div>
              <label className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">
                <FileText className="h-3.5 w-3.5 text-slate-400" /> Description / problem summary
              </label>
              <textarea
                rows={3}
                data-testid="new-job-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's happening? Any symptoms, model numbers, access notes, etc."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13.5px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 max-[767px]:bg-white max-[767px]:text-slate-700 max-[767px]:placeholder:text-slate-400 max-[767px]:rounded-xl"
              />
            </div>
          </section>

          {error && (
            <div data-testid="new-job-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
              {error}
            </div>
          )}

          {/* Hidden submit button enables Enter-to-submit from any input */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>

        {/* Footer (desktop) */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40 max-[767px]:hidden">
          <div className="text-[11.5px] text-slate-500">
            Tech will be <strong className="text-slate-700">Unassigned</strong> — assign from the Dispatch Board.
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="new-job-cancel" className="h-9 text-[13px]">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="new-job-submit"
              className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1.5" /> Save job
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Footer (mobile) */}
        <div className="hidden max-[767px]:flex max-[767px]:flex-col gap-3 px-4 pb-5 pt-3 bg-[#FAFBFC]">
          <div className="flex items-start gap-2 px-3.5 py-3 rounded-2xl bg-amber-50 border border-amber-200">
            <Info className="h-4 w-4 text-[#1484F6] shrink-0 mt-0.5" />
            <div className="text-[12px] text-slate-600">
              Tech will be <strong className="text-slate-700">Unassigned</strong> — assign from the Dispatch Board.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="new-job-cancel-mobile"
              className="flex-1 h-12 rounded-full text-[14px] font-semibold bg-slate-100 border-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="new-job-submit-mobile"
              className="flex-1 h-12 rounded-full text-[14px] font-semibold bg-[#1484F6] hover:bg-[#0f6fd6] text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1.5" /> Save Job
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {confirmJob && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-4" data-testid="confirm-text-popup">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-sm p-5 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <MessageSquareText className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-[15px] font-semibold text-slate-900">Send a booking confirmation text?</div>
            <p className="text-[12.5px] text-slate-500 mt-1">
              {confirmJob.customer} will get a text at <span className="font-semibold text-slate-700">{phone.trim()}</span> with
              the date and arrival window for job {confirmJob.id}.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                data-testid="confirm-text-skip"
                disabled={sendingText}
                onClick={() => finishAfterConfirm(confirmJob)}
                className="h-9 text-[13px]"
              >
                No thanks
              </Button>
              <Button
                type="button"
                data-testid="confirm-text-send"
                disabled={sendingText}
                onClick={sendConfirmText}
                className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px] font-semibold"
              >
                {sendingText ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <MessageSquareText className="h-4 w-4 mr-1.5" />}
                Send text
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, mobileIcon, label, mobileLabel, hint, hintTone, mobileToggle }) {
  const hintColor = hintTone === "amber" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-blue-700 bg-blue-50 border-blue-200";
  const MobileIcon = mobileIcon || Icon;
  return (
    <>
      <div className="flex items-center justify-between max-[767px]:hidden">
        <div className="flex items-center gap-1.5 text-[12px] uppercase tracking-wider font-semibold text-slate-500">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        {hint && (
          <span className={`text-[10.5px] font-medium px-1.5 py-0.5 rounded border ${hintColor}`}>{hint}</span>
        )}
      </div>
      <div className={`hidden max-[767px]:flex ${mobileToggle ? "flex-col gap-2" : "items-center justify-between"}`}>
        <div className="flex items-center gap-2 text-[15px] font-bold text-[#173B89]">
          <MobileIcon className="h-[18px] w-[18px]" /> {mobileLabel || label}
        </div>
        {mobileToggle}
      </div>
    </>
  );
}

function CustomerModeToggle({ isExisting, onPickExisting, onPickNew }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPickExisting}
        data-testid="new-job-mode-existing"
        className={`flex-1 px-3 py-1.5 rounded-full border text-[11px] font-semibold text-center ${
          isExisting ? "bg-[#1484F6] border-[#1484F6] text-white" : "bg-transparent border-slate-300 text-slate-500"
        }`}
      >
        Existing Customer
      </button>
      <button
        type="button"
        onClick={onPickNew}
        data-testid="new-job-mode-new"
        className={`flex-1 px-3 py-1.5 rounded-full border text-[11px] font-semibold text-center ${
          !isExisting ? "bg-[#1484F6] border-[#1484F6] text-white" : "bg-transparent border-slate-300 text-slate-500"
        }`}
      >
        New Customer
      </button>
    </div>
  );
}

function Field({ label, icon: Icon, mobileIcon, mobileClassName = "", children }) {
  const MobileIcon = mobileIcon || Icon;
  return (
    <div className={mobileClassName}>
      <label className="block text-[12px] font-semibold text-slate-700 mb-1.5 max-[767px]:text-[#173B89]">{label}</label>
      <div className="relative">
        {Icon && <Icon className={`h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${mobileIcon ? "max-[767px]:hidden" : ""}`} />}
        {mobileIcon && <MobileIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none hidden max-[767px]:block" />}
        {children}
      </div>
    </div>
  );
}
